import { toWorkspacePath } from "@/features/Workspaces/workspaceRoutes";
// src/features/time_serie_file_dates/new.tsx

import { Form } from "@/components/ui/form";
import { useEditForm } from "@/features/Edit/hooks";
import { FormActionsUpdate } from "@/features/FormCard/Actions";
import { Diff } from "@/features/FormCard/Diff";
import {
  CheckboxField,
  DatePickerField,
  InputField,
  SelectField,
} from "@/features/FormCard/Fields";
import {
  ConfirmationModal,
  DeleteConfirmationModal,
} from "@/features/FormCard/Modals";
import {
  dateSchema,
  parseToDateValue,
  parseYes,
} from "@/features/FormCard/utils";
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Divider,
  Link,
} from "@heroui/react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { statusOptions } from "./filter";

/**
 * Zod schema for the New TimeSerieFileDate form.
 * All fields optional unless required in the original Ruby form.
 */
const FormSchema = z.object({
  status: z.string().min(1, "Status is required"),
  valuation_date: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  generation_date: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  strategy_id: z.coerce.number().optional().nullable(),
  time_serie_file_id: z.coerce.number().optional().nullable(),
  filename: z.string().min(1, "Filename is required"),
  extracted_validated: z.preprocess(
    (val) => parseYes(val as string),
    z.boolean().optional(),
  ),
  enriched_validated: z.preprocess(
    (val) => parseYes(val as string),
    z.boolean().optional(),
  ),
  inquiring: z.preprocess(
    (val) => parseYes(val as string),
    z.boolean().optional(),
  ),
  is_python_etl: z.preprocess(
    (val) => parseYes(val as string),
    z.boolean().optional(),
  ),
});

export function TimeSerieFileDateEditCard({
  id,
  client,
  table_name,
}: {
  id: number;
  client: string;
  table_name: string;
}) {
  const { t } = useTranslation();

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
    handleDelete,
    isDeleting,
  } = useEditForm({
    disable_translations_request: true,
    id,
    client,
    table_name,
    FormSchema,
  });

  const timeSerieFileId = form.watch("time_serie_file_id");
  const strategyId = form.watch("strategy_id");

  return (
    <div className="grid place-items-center h-full w-full max-w-card">
      <div className="flex justify-center w-full">
        <Card className="p-4 bg-white w-full">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                {t(`${table_name}.title`, {
                  ns: "tables",
                  defaultValue: "Time Serie File Date",
                })}{" "}
                {id}
              </CardHeader>
              <Divider />
              <CardBody className="flex flex-col gap-4">
                {/* Row 1 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="filename"
                    label={t(`${table_name}.columns.filename`, {
                      ns: "tables",
                      defaultValue: "Filename",
                    })}
                    isRequired
                    fieldOptions={{
                      isDisabled: true,
                    }}
                    control={form.control}
                  />
                  <InputField
                    name="time_serie_file_id"
                    label={t(`${table_name}.columns.time_serie_file_id`, {
                      ns: "tables",
                      defaultValue: "Time Serie File ID",
                    })}
                    endContent={
                      <Button
                        href={toWorkspacePath(
                          `/admin/qtl - etl/time_serie_files/${timeSerieFileId}`,
                        )}
                        variant="light"
                        color="primary"
                        showAnchorIcon
                        as={Link}
                        className="
                          bg-transparent shadow-none p-0 min-w-0 h-6 w-6
                        "
                      ></Button>
                    }
                    fieldOptions={{
                      isReadOnly: true,
                    }}
                    type="number"
                    control={form.control}
                  />
                </div>
                {/* Row 2 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="strategy_id"
                    label={t(`${table_name}.columns.strategy_id`, {
                      ns: "tables",
                      defaultValue: "Strategy ID",
                    })}
                    type="number"
                    endContent={
                      <Button
                        href={toWorkspacePath(
                          `/admin/qtl - referential/strategies/${strategyId}`,
                        )}
                        variant="light"
                        color="primary"
                        showAnchorIcon
                        as={Link}
                        className="
                          bg-transparent shadow-none p-0 min-w-0 h-6 w-6
                        "
                      ></Button>
                    }
                    fieldOptions={{ isReadOnly: true }}
                    control={form.control}
                  />
                  <SelectField
                    name="status"
                    label={t(`${table_name}.columns.status`, {
                      ns: "tables",
                      defaultValue: "Status",
                    })}
                    options={statusOptions}
                    control={form.control}
                    allow_empty_option
                  />
                </div>
                {/* Row 3 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <DatePickerField
                    name="valuation_date"
                    label={t(`${table_name}.columns.valuation_date`, {
                      ns: "tables",
                      defaultValue: "Valuation Date",
                    })}
                    control={form.control}
                  />
                  <DatePickerField
                    name="generation_date"
                    label={t(`${table_name}.columns.generation_date`, {
                      ns: "tables",
                      defaultValue: "Generation Date",
                    })}
                    control={form.control}
                  />
                </div>
                {/* Row 4 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <CheckboxField
                    name="extracted_validated"
                    label={t(`${table_name}.columns.extracted_validated`, {
                      ns: "tables",
                      defaultValue: "Extracted Validated",
                    })}
                    control={form.control}
                  />
                  <CheckboxField
                    name="enriched_validated"
                    label={t(`${table_name}.columns.enriched_validated`, {
                      ns: "tables",
                      defaultValue: "Enriched Validated",
                    })}
                    control={form.control}
                  />
                </div>
                {/* Row 5 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <CheckboxField
                    name="inquiring"
                    label={t(`${table_name}.columns.inquiring`, {
                      ns: "tables",
                      defaultValue: "Inquiring",
                    })}
                    control={form.control}
                  />
                  <CheckboxField
                    name="is_python_etl"
                    label={t(`${table_name}.columns.is_python_etl`, {
                      ns: "tables",
                      defaultValue: "Is Python ETL",
                    })}
                    control={form.control}
                  />
                </div>
              </CardBody>

              <CardFooter>
                <FormActionsUpdate
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                  isDirty={form.formState.isDirty}
                  elementId={id}
                  client={client}
                  allowDelete={false}
                />
              </CardFooter>
            </form>
          </Form>

          <ConfirmationModal
            isOpen={isConfirmationModalOpen}
            isLoading={isUpdating}
            onClose={cancelUpdate}
            onConfirm={confirmUpdate}
            title={t("confirm_update")}
          >
            {changedFields ? Diff(changedFields, []) : "No changes detected"}
          </ConfirmationModal>

          <DeleteConfirmationModal
            isOpen={isDeleteModalOpen}
            onClose={closeDeleteModal}
            onDelete={handleDelete}
            isLoading={isDeleting}
          />
        </Card>
      </div>
    </div>
  );
}
